import { PermissionsTypes } from '@back-office/http';
import {
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Menu,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSION_VOID_REFUND_MAXIMUM } from '../../common';
import ArrowDownIcon from '../../icons/arrow-down';
import GcpBox from '../gcp-box';
import useViewModel from './viewmodel';

type GcpPermissionDropdownProps = {
  currentPermission: number[];
  hiddenField: boolean;
  voidRefundCount: number;
  permissionsList: PermissionsTypes[];
  isRecurringMerchant: boolean;
  handleOnChange: (
    permissionId: number | number[],
    newPermissionId: number[],
    isPermissionLimited: boolean
  ) => void;
};

const useStyles = makeStyles(
  (theme) => ({
    dropdown: {
      color: theme.palette.common.mineShaft,
    },
    menu: {
      marginTop: '8px',
    },
    error: {
      borderColor: '#EB5757',
    },
    validation_message: {
      fontSize: '12px',
      color: '#EB5757',
      textAlign: 'start',
    },
    p: {
      margin: 0,
      textAlign: 'left',
    },
    chip: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    caption: {
      color: '#BDBDBD',
    },
  }),
  { name: 'GcpPermissionDropdown' }
);

export interface GroupedPermissions {
  paymentUpload: PermissionsTypes[];
  recurringRequest: PermissionsTypes[];
  refund: PermissionsTypes[];
}

const GcpPermissionDropdown: FC<GcpPermissionDropdownProps> = ({
  currentPermission,
  hiddenField,
  voidRefundCount,
  permissionsList,
  isRecurringMerchant,
  handleOnChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    anchorEl,
    handleClick,
    handleClose,
    handleCheckboxChange,
    handleRecurringRequestChange,
    handleUploadPaymentChange,
    menuStyles,
    newPermissions,
    refundPermission,
    recurringPermission,
    uploadPaymentPermission,
    selectedPermissionsCount,
    labelMapPermissions,
    isVoidRefundChecked,
    changeToPermissionFormat,
    groupedPermissions,
  } = useViewModel({
    currentPermission,
    handleOnChange,
    voidRefundCount,
    isRecurringMerchant,
    hiddenField,
    permissionsList,
  });

  return (
    <>
      {hiddenField ? (
        <Typography className={classes.p}>
          {refundPermission && refundPermission !== '0' && (
            <>
              {t(labelMapPermissions(refundPermission))} <br />
            </>
          )}

          {isRecurringMerchant &&
            recurringPermission &&
            recurringPermission !== '0' && (
              <>
                {`${t('invite_new_user.permissions.recurring_enrollment')}: ${t(
                  labelMapPermissions(recurringPermission)
                )}`}
                <br />
              </>
            )}

          {isRecurringMerchant &&
            uploadPaymentPermission &&
            uploadPaymentPermission !== '0' && (
              <>
                {`${t('invite_new_user.permissions.payment_file')}: ${t(
                  labelMapPermissions(uploadPaymentPermission)
                )}`}
              </>
            )}

          {(_.isEmpty(currentPermission) ||
            (!isRecurringMerchant && refundPermission === '0')) &&
            t(`invite_new_user.permissions.n_a`)}
        </Typography>
      ) : (
        <GcpBox alignItems="center" display="flex" gap={1}>
          <FormControl>
            <GcpBox alignItems="center" display="flex" gap={1}>
              <Button
                className={`dropdown ${
                  voidRefundCount >= PERMISSION_VOID_REFUND_MAXIMUM &&
                  isVoidRefundChecked &&
                  !currentPermission.includes(1)
                    ? classes.error
                    : ''
                }`}
                endIcon={<ArrowDownIcon />}
                variant="outlined"
                onClick={handleClick}
              >
                {t(`invite_new_user.permissions.select`)}
              </Button>

              <Menu
                keepMounted
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical:
                    anchorEl &&
                    anchorEl.getBoundingClientRect().bottom >
                      window.innerHeight / 2
                      ? 'top'
                      : 'bottom',
                  horizontal: 'center',
                }}
                className={classes.menu}
                elevation={1}
                getContentAnchorEl={null}
                id="customized-menu"
                open={Boolean(anchorEl)}
                style={menuStyles}
                transformOrigin={{
                  vertical:
                    anchorEl &&
                    anchorEl.getBoundingClientRect().bottom >
                      window.innerHeight / 2
                      ? 'bottom'
                      : 'top',
                  horizontal: 'center',
                }}
                onClose={handleClose}
              >
                <GcpBox display="block" gap={2} style={{ padding: '0 8px 0' }}>
                  <Grid>
                    <Typography className={classes.caption} variant="caption">
                      {t(`invite_new_user.permissions.refund`)}
                    </Typography>
                  </Grid>
                  <Grid>
                    {groupedPermissions?.refund.map((item) => (
                      <FormControlLabel
                        key={item.name}
                        control={
                          <Checkbox
                            checked={newPermissions.includes(`${item.id}`)}
                            color="primary"
                            name={`${item.id}`}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              handleCheckboxChange(event, `${item.id}`)
                            }
                          />
                        }
                        label={t(
                          `invite_new_user.permissions.${changeToPermissionFormat(
                            item.name
                          )}`
                        )}
                      />
                    ))}
                  </Grid>
                </GcpBox>
                {isRecurringMerchant && (
                  <div>
                    <Divider />
                    <GcpBox
                      display="block"
                      gap={2}
                      style={{ padding: '6px 8px 0' }}
                    >
                      <Grid>
                        <Typography
                          className={classes.caption}
                          variant="caption"
                        >
                          {t(
                            `invite_new_user.permissions.recurring_enrollment`
                          )}
                        </Typography>
                      </Grid>
                      <Grid>
                        <FormControl component="fieldset">
                          <RadioGroup
                            aria-label="recurringRequest"
                            value={recurringPermission}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              handleRecurringRequestChange(event.target.value)
                            }
                          >
                            <FormControlLabel
                              key="n/a"
                              control={<Radio color="primary" />}
                              label="No permission"
                              value="0"
                            />
                            {groupedPermissions?.recurringRequest.map(
                              (item) => (
                                <FormControlLabel
                                  key={item.name}
                                  control={<Radio color="primary" />}
                                  label={t(
                                    `invite_new_user.permissions.${changeToPermissionFormat(
                                      item.name
                                    )}`
                                  )}
                                  value={`${item.id}`}
                                />
                              )
                            )}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </GcpBox>

                    <Divider />
                    <GcpBox
                      display="block"
                      gap={2}
                      style={{ padding: '6px 8px 0' }}
                    >
                      <Grid>
                        <Typography
                          className={classes.caption}
                          variant="caption"
                        >
                          {t(`invite_new_user.permissions.payment_file`)}
                        </Typography>
                      </Grid>
                      <Grid>
                        <FormControl component="fieldset">
                          <RadioGroup
                            aria-label="paymentUpload"
                            value={uploadPaymentPermission}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              handleUploadPaymentChange(event.target.value)
                            }
                          >
                            <FormControlLabel
                              key="n/a"
                              control={<Radio color="primary" />}
                              label="No permission"
                              value="0"
                            />
                            {groupedPermissions?.paymentUpload.map((item) => (
                              <FormControlLabel
                                key={item.name}
                                control={<Radio color="primary" />}
                                label={t(
                                  `invite_new_user.permissions.${changeToPermissionFormat(
                                    item.name
                                  )}`
                                )}
                                value={`${item.id}`}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </GcpBox>
                  </div>
                )}
              </Menu>

              <Chip
                className={classes.chip}
                label={selectedPermissionsCount.toString()}
                variant="default"
              />
            </GcpBox>
            {/* TODO: need to check again after void/refund more than 3 */}
            {voidRefundCount >= PERMISSION_VOID_REFUND_MAXIMUM &&
            isVoidRefundChecked &&
            !currentPermission.includes(1) ? (
              <GcpBox className={classes.validation_message}>
                {t('invite_new_user.permissions_validation_message', {
                  permissionMaximum: PERMISSION_VOID_REFUND_MAXIMUM,
                })}
              </GcpBox>
            ) : null}
          </FormControl>
        </GcpBox>
      )}
    </>
  );
};

export default GcpPermissionDropdown;
